/**
 * Smart Date Pattern Generator - Generates search patterns from recognized dates
 * 
 * Converts recognition results into search patterns with boost values
 */

"use strict";

/**
 * Generate date patterns for search conditions
 * @param {Object} recognitionResult - Result from recognizeDate()
 * @returns {Array} Array of date patterns with boost values
 */
export function generateDatePatterns(recognitionResult) {
    if (!recognitionResult) return [];
    
    const patterns = [];
    const pad2 = (n) => String(n).padStart(2, '0');
    const BOOST_DATE = 2;
    
    // Exact date
    if (recognitionResult.from && recognitionResult.to && recognitionResult.from === recognitionResult.to) {
        patterns.push({
            pattern: recognitionResult.from,
            boost: BOOST_DATE,
            searchInDateField: true
        });
    }
    // Date range
    else if (recognitionResult.from && recognitionResult.to) {
        const fromParts = recognitionResult.from.split('-');
        const toParts = recognitionResult.to.split('-');
        
        if (fromParts.length === 3 && toParts.length === 3) {
            // Same year and month
            if (fromParts[0] === toParts[0] && fromParts[1] === toParts[1]) {
                patterns.push({
                    pattern: `${fromParts[0]}-${fromParts[1]}-\\d{2}`,
                    boost: BOOST_DATE,
                    searchInDateField: true
                });
            }
            // Same year
            else if (fromParts[0] === toParts[0]) {
                patterns.push({
                    pattern: `${fromParts[0]}-\\d{2}-\\d{2}`,
                    boost: BOOST_DATE,
                    searchInDateField: true
                });
            }
            // Full range
            else {
                patterns.push({
                    pattern: `\\d{4}-\\d{2}-\\d{2}`,
                    boost: BOOST_DATE,
                    searchInDateField: true
                });
            }
        }
    }
    
    // Variants (day-month without year, ambiguous dates)
    if (recognitionResult.variants && Array.isArray(recognitionResult.variants)) {
        recognitionResult.variants.forEach(variant => {
            if (variant.pattern) {
                patterns.push({
                    pattern: variant.pattern,
                    boost: BOOST_DATE,
                    searchInDateField: true
                });
            }
        });
    }
    
    // CRITICAL: Generate patterns for YYMM[A-Z]DD format (e.g., "5311C26")
    // This format is used in filenames and should be searched in filename field, not date field
    if (recognitionResult.from && recognitionResult.to && recognitionResult.from === recognitionResult.to) {
        const fromParts = recognitionResult.from.split('-');
        if (fromParts.length === 3) {
            const year = fromParts[0];
            const month = pad2(parseInt(fromParts[1], 10));
            const day = pad2(parseInt(fromParts[2], 10));
            const shortYear = year.slice(-2); // "53" for 1953
            
            // Check if original query contains YYMM[A-Z]DD format
            // Try multiple sources: recognitionResult.query, recognitionResult.strippedQuery, or check usedTokens
            const originalQuery = recognitionResult.query || recognitionResult.strippedQuery || '';
            const yymmLetterDdMatch = originalQuery.match(/(\d{2})(\d{2})([A-Za-z])(\d{2})/);
            
            // Also check if usedTokens contains a letter (indicates YYMM[A-Z]DD format)
            const hasLetterInTokens = recognitionResult.usedTokens && 
                                     recognitionResult.usedTokens.some(t => /^[A-Za-z]$/.test(t));
            
            // Generate patterns for YYMM[A-Z]DD format if detected
            if (yymmLetterDdMatch) {
                const yy = yymmLetterDdMatch[1];
                const mm = yymmLetterDdMatch[2];
                const letter = yymmLetterDdMatch[3];
                const dd = yymmLetterDdMatch[4];
                
                // Verify this matches the recognized date
                if (shortYear === yy && month === mm && day === dd) {
                    // Generate patterns for YYMM[A-Z]DD format (filename search, not date field)
                    patterns.push({
                        pattern: `${yy}${mm}${letter}${dd}`, // Exact match: "5311C26"
                        boost: BOOST_DATE,
                        searchInDateField: false // Search in filename, not date field
                    });
                    patterns.push({
                        pattern: `${yy}${mm}[A-Za-z]${dd}`, // Any letter: "5311C26", "5311M26", etc.
                        boost: BOOST_DATE - 1, // Slightly lower boost for generic pattern
                        searchInDateField: false
                    });
                }
            } else if (hasLetterInTokens && recognitionResult.usedTokens && recognitionResult.usedTokens.length >= 3) {
                // Fallback: if usedTokens contains a letter, try to reconstruct YYMM[A-Z]DD pattern
                // This handles cases where the query was already processed
                const tokens = recognitionResult.usedTokens;
                const letterToken = tokens.find(t => /^[A-Za-z]$/.test(t));
                if (letterToken && shortYear && month && day) {
                    // Generate patterns with the letter from tokens
                    patterns.push({
                        pattern: `${shortYear}${month}${letterToken}${day}`, // Exact match: "5311C26"
                        boost: BOOST_DATE,
                        searchInDateField: false
                    });
                    patterns.push({
                        pattern: `${shortYear}${month}[A-Za-z]${day}`, // Any letter
                        boost: BOOST_DATE - 1,
                        searchInDateField: false
                    });
                }
            }
        }
    }
    
    return patterns;
}

// Global export for backward compatibility
window.generateDatePatterns = generateDatePatterns;

